import React, { SetStateAction, Dispatch } from 'react';
import cx from 'classnames';
import { styled } from '@mui/material/styles';
import { Grid, Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { borderRadius } from 'new/constants/styles';
import { PermissionType } from 'new/modules/OfficeSettings/types';
import IconFontAwesome from 'new/components/IconFontAwesome';
import { TENANT } from 'new/tenants';

type Props = {
  name: string;
  cardId: PermissionType;
  permissions: string[];
  isSelected: boolean;
  isPlanCoordinator: boolean;
  setIsPlanCoordinator: Dispatch<SetStateAction<boolean>>;
  isUserPlanCoordinator: boolean;
  isUserBusinessOwner: boolean;
  isUserSuperAdmin: boolean;
  isSuperAdmin: boolean;
  toggleIsSuperAdmin: () => void;
  setSelectedPermissionId: (isSelected: PermissionType) => void;
  isAdminCard?: boolean;
  isMultiLocation?: boolean;
};

const PermissionsCardWrapper = styled(Grid)(({ theme }) => ({
  fontFamily: TENANT.theme.modal.fontFamily,
  alignItems: 'center',
  background: '#EEF0F3',
  width: 700,
  minHeight: 100,
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  border: `4px solid transparent`,
  borderRadius,
  cursor: 'pointer',

  '&.isSelected': {
    border: `4px solid ${theme.palette.primary.main}`,
  },

  '.name': {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(4),
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },

  '.permissionsList': {
    fontSize: '0.875rem',
    color: theme.palette.grey[600],
    margin: 0,

    li: {
      lineHeight: '1.3rem',
    },
  },

  '.MuiFormControlLabel-root': {
    height: 42,
    margin: 0,

    '.MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      color: theme.palette.grey[600],
    },
  },

  '.adminExtraPermissions': {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 4,

    '.extraPermission': {
      height: 24,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontSize: '0.875rem',
      color: theme.palette.grey[600],
      marginLeft: theme.spacing(1),
      whiteSpace: 'pre',
    },

    '.MuiFormControlLabel-root': {
      height: 24,
    },
  },
}));

const renderPlanCoordinator = ({
  isSelected,
  isPlanCoordinator,
  setIsPlanCoordinator,
  isUserSuperAdmin,
  isUserPlanCoordinator,
  isUserBusinessOwner,
}: {
  isPlanCoordinator: boolean;
  setIsPlanCoordinator: Dispatch<SetStateAction<boolean>>;
  isUserSuperAdmin: boolean;
  isUserPlanCoordinator: boolean;
  isUserBusinessOwner: boolean;
  isSelected?: boolean;
}) => {
  if (
    isSelected &&
    (isUserPlanCoordinator || isUserBusinessOwner || isUserSuperAdmin)
  ) {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={isPlanCoordinator}
            onChange={() =>
              setIsPlanCoordinator((isPlanCoordinator) => !isPlanCoordinator)
            }
          />
        }
        label="Plan Coordinator"
      />
    );
  } else {
    return (
      <Box className="extraPermission">
        <Box width={26} textAlign="center">
          {isPlanCoordinator && (
            <IconFontAwesome name="faCheckSolid" size="1x" />
          )}
        </Box>
        Plan Coordinator
      </Box>
    );
  }
};

const renderSuperAdmin = ({
  isUserBusinessOwner,
  isUserSuperAdmin,
  isSelected,
  isSuperAdmin,
  toggleIsSuperAdmin,
}: {
  isUserBusinessOwner: boolean;
  isUserSuperAdmin: boolean;
  isSelected?: boolean;
  isSuperAdmin: boolean;
  toggleIsSuperAdmin: () => void;
}) => {
  if (isSelected && (isUserBusinessOwner || isUserSuperAdmin)) {
    return (
      <Box width={122}>
        <Tooltip
          placement="top"
          title="Check Super Admin only if user needs access to all practice locations."
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected && isSuperAdmin}
                onChange={toggleIsSuperAdmin}
              />
            }
            label="Super Admin"
          />
        </Tooltip>
      </Box>
    );
  } else {
    return (
      <Box className="extraPermission">
        <Box width={26} textAlign="center">
          {isSuperAdmin && <IconFontAwesome name="faCheckSolid" size="1x" />}
        </Box>
        Super Admin
      </Box>
    );
  }
};

// const renderCustomData = (
//   <Box className="adminExtraPermissions">
//       <Box className="extraPermission">
//         <Box width={26} textAlign="center">
//         </Box>
//       </Box>
//       <Box className="extraPermission">
//         <Box width={26} textAlign="center">
//         </Box>
//       </Box>
//   </Box>
// )

const PermissionsCard: React.FC<Props> = ({
  cardId,
  name,
  permissions,
  isSelected,
  setSelectedPermissionId,
  isAdminCard,
  isPlanCoordinator,
  setIsPlanCoordinator,
  isUserPlanCoordinator,
  isUserBusinessOwner,
  isUserSuperAdmin,
  isSuperAdmin,
  toggleIsSuperAdmin,
  isMultiLocation,
}) => (
  <PermissionsCardWrapper
    onClick={() => {
      setSelectedPermissionId(cardId);
    }}
    className={cx({ isSelected })}
    container
  >
    <Grid item xs={5}>
      <Box className="name">{name}</Box>
      {
        isAdminCard && (
          // isSelected ? (
          <Box className="adminExtraPermissions">
            {renderPlanCoordinator({
              isSelected,
              isPlanCoordinator,
              setIsPlanCoordinator,
              isUserPlanCoordinator,
              isUserBusinessOwner,
              isUserSuperAdmin,
            })}

            {renderSuperAdmin({
              isSelected,
              isSuperAdmin,
              toggleIsSuperAdmin,
              isUserBusinessOwner,
              isUserSuperAdmin,
            })}

            {isMultiLocation && (
              <Box className="extraPermission">
                <Box width={26} textAlign="center"></Box>
              </Box>
            )}
          </Box>
        )
        // : (renderCustomData)
        // )
      }
    </Grid>
    <Grid item xs={7}>
      <ul className="permissionsList">
        {permissions.map((permission) => (
          <li key={permission}>{permission}</li>
        ))}
      </ul>
    </Grid>
  </PermissionsCardWrapper>
);

export default PermissionsCard;
